import { isArray } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { MedicalLeavePuePayer, PueFault } from '@dreryk/egabinet-ezla-frontend-api';
import { GuardianExternalIdentifier, PatientExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import {
   createInterpolatedTranslationKey,
   createSingleInterpolatedTranslationKey,
   TranslatableText,
   useTranslate,
} from '@dreryk/react-common';
import { EmptyStatePlaceholder, LoaderSpinner, Multiselect } from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';

import { usePatientQuery } from '../../../../../common/common.hooks';
import { commonTranslationKeys } from '../../../../../translations';
import { MedicalLeaveCommonFormController } from '../../common.types';
import { MedicalLeaveFormSection } from '../MedicalLeaveFormSection';

import { MedicalLeaveFormPayersTable } from './MedicalLeaveFormPayersTable';
import { usePuePayers } from './payerSection.hooks';
import { PuePayerMultiselectItem } from './PuePayerMultiselectItem';

type MedicalLeaveFormPayerSectionProps = {
   readonly patientId: number;
   readonly controller: MedicalLeaveCommonFormController;
   readonly externalIdentifier?: PatientExternalIdentifier | GuardianExternalIdentifier | null;
   readonly noExternalIdentifierPlaceholder: TranslatableText;
   readonly title: TranslatableText;
   readonly onPueFault: (fault: PueFault) => void;
};

const useTriggerLabelExtractor = (
   availablePayersCount: number | undefined
): ((selectedValues: MedicalLeavePuePayer[]) => string) => {
   const translate = useTranslate();
   return useCallback(
      (selectedPayers) => {
         if (selectedPayers.length === 0) {
            return '';
         }
         if (availablePayersCount === selectedPayers.length) {
            return translate(
               createSingleInterpolatedTranslationKey(
                  commonTranslationKeys.allPayersChosen,
                  'count',
                  availablePayersCount
               )
            );
         }
         return translate(
            createInterpolatedTranslationKey(commonTranslationKeys.chosenPayers, {
               count: selectedPayers.length,
               countBasedSuffix: translate(
                  commonTranslationKeys[selectedPayers.length === 1 ? 'payerAccusative' : 'payersPlural']
               ),
            })
         );
      },
      [availablePayersCount, translate]
   );
};

const StyledMultiselect = styled(Multiselect)`
   max-width: 400px;
` as typeof Multiselect;

const puePayersSelectAllCheckboxRenderer = (items: MedicalLeavePuePayer[] | undefined): JSX.Element => (
   <Multiselect.SelectAll testId={'selectAll'} items={items ?? []} selectAllLabel={commonTranslationKeys.allPayers} />
);

// eslint-disable-next-line max-lines-per-function
export const MedicalLeaveFormPayerSection = (props: MedicalLeaveFormPayerSectionProps): JSX.Element => {
   const { data: patient, loading: isPatientLoading } = usePatientQuery(props.patientId);
   const { insuranceType, payers } = props.controller.watch(['insuranceType', 'payers']);
   const payersController = props.controller.register('payers', {
      required: true,
      validate: (data) => (isArray(data) && data.length ? undefined : 'components:fieldRequired'),
   });

   useEffect(() => {
      props.controller.setValue('payers', []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [insuranceType]);

   const patientExternalIdentifier = useMemo<PatientExternalIdentifier | undefined>(() => {
      if (props.externalIdentifier instanceof PatientExternalIdentifier) {
         return props.externalIdentifier;
      }

      if (props.externalIdentifier instanceof GuardianExternalIdentifier) {
         return PatientExternalIdentifier.of(
            props.externalIdentifier.accept<Pesel | Passport | NationalIdentificationNumber>({
               visitPesel: (pesel) => pesel,
               visitPassport: (passport) => passport,
               visitNationalIdentificationNumber: (nationalIdentificationNumber) => nationalIdentificationNumber,
            })
         );
      }

      return undefined;
   }, [props.externalIdentifier]);

   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const [payersSearchCreator, puePayers] = usePuePayers(
      patient?.identityDocument,
      patientExternalIdentifier,
      insuranceType,
      openAccountAuthorizationModal,
      props.onPueFault
   );

   const triggerLabelExtractor = useTriggerLabelExtractor(puePayers?.length);
   const shouldDisplayPayersTable = Boolean(payers?.length && patientExternalIdentifier);

   return (
      <MedicalLeaveFormSection title={props.title} withRequiredMark>
         {isPatientLoading ? (
            <LoaderSpinner />
         ) : (
            <>
               {patientExternalIdentifier ? (
                  <StyledMultiselect<MedicalLeavePuePayer>
                     placeholder={commonTranslationKeys.chooseOneOrMorePayers}
                     triggerLabelExtractor={triggerLabelExtractor}
                     itemKeyGenerator={'identificationNumber'}
                     controller={payersController}
                     testId={'payers'}
                     defaultValue={payers}
                  >
                     <Multiselect.SearchCreator<MedicalLeavePuePayer>
                        searchCreator={payersSearchCreator}
                        noResultsMessage={commonTranslationKeys.noPuePayers}
                        selectAllCheckboxRenderer={puePayersSelectAllCheckboxRenderer}
                     >
                        {({ items }) => (
                           <Multiselect.List items={items} keyExtractor={'identificationNumber'}>
                              {(payer) => <PuePayerMultiselectItem payer={payer} />}
                           </Multiselect.List>
                        )}
                     </Multiselect.SearchCreator>
                  </StyledMultiselect>
               ) : (
                  <EmptyStatePlaceholder
                     heading={props.noExternalIdentifierPlaceholder}
                     testId={'noExternalIdentifier'}
                  />
               )}
               {shouldDisplayPayersTable ? (
                  <MedicalLeaveFormPayersTable controller={props.controller} selectedPayers={payers ?? []} />
               ) : null}
            </>
         )}
         {renderAccountAuthorizationnModal({
            onAuthorization: payersSearchCreator(),
         })}
      </MedicalLeaveFormSection>
   );
};
